import objectPath from 'object-path'
import cloneDeep from 'clone-deep'
import { getApiCondition } from 'designer/component/action/Option'
import {
  deleteData,
  fetchAfterSuccess,
  getData,
  mapResponseData,
  patchData,
  postData,
  putData,
} from 'designer/component/action/Api'

const pendingCallApiActions = {}

export const isRuleCallApiAction = (action = {}) => {
  return ['callAPI', 'callApi'].includes(action.type)
}

export const isRuleCallApiWaitMode = (action = {}) => {
  if (!isRuleCallApiAction(action)) return false

  return !['Background', 'No Wait', 'NoWait'].includes(action.mode)
}

const getRuleCallApiKey = (action = {}) => {
  return (
    action.target ||
    action.key ||
    action.callApi ||
    action.callAPI ||
    action.apiKey ||
    ''
  )
}

const getRuleCallApiActionKey = (rule = {}, action = {}) => {
  return [
    rule.id || '',
    action.id || '',
    getRuleCallApiKey(action),
  ]
    .filter((key) => key)
    .join(':')
}

const getRuleApiCondition = (jsonForm = {}, isUiChanged = {}) => {
  let apiGroup = getApiCondition(jsonForm)
  let tabForm = isUiChanged?.tabForm

  if (tabForm) {
    Object.values(tabForm).forEach(({ jsonForm }) => {
      let tabApiGroup = getApiCondition(jsonForm)
      apiGroup = [...apiGroup, ...tabApiGroup]
    })
  }

  return apiGroup
}

const getRuleCallApi = (designContext = {}, action = {}) => {
  let { jsonForm = {}, isUiChanged = {} } = designContext
  let apiKey = getRuleCallApiKey(action)

  return getRuleApiCondition(jsonForm, isUiChanged).find(
    (api) => api.key === apiKey,
  )
}

const getRuleCallApiData = (
  designContext = {},
  data = {},
  formData = {},
) => {
  let {
    searchData = {},
    designerData = {},
    urlParams = {},
    tabParamActive = {},
    user = {},
  } = designContext
  let datasourceValue =
    data.datasourceValue ||
    data.datasource ||
    objectPath.get(designContext, '@datasource') ||
    designContext.datasourceValue ||
    {}

  return {
    ...data,
    searchData,
    formData,
    urlParams,
    designerData,
    datasourceValue,
    tabParamActive,
    user,
  }
}

const getRuleCallApiRequestFormData = (
  designContext = {},
  data = {},
) => {
  let { jsonForm = {}, isUiChanged = {} } = designContext
  let formData = data.formData || jsonForm.formData || {}
  let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')

  if (!isWaitUpsert?.status) return formData

  let { isModalProp, isReplace, key, table = [], rowData = [] } = isWaitUpsert
  let dataCheck = cloneDeep(formData)
  let tableFilterDelete = table.filter((rowData) => {
    if (objectPath.has(rowData, '@isDeleted')) {
      return !objectPath.get(rowData, '@isDeleted')
    }

    return true
  })

  objectPath.set(dataCheck, `@isWaitUpsert.${key}`, rowData)
  objectPath.set(dataCheck, `@isConfirmUpsert.${key}`, table)
  if (isModalProp) {
    if (isReplace) {
      objectPath.set(
        dataCheck,
        key,
        table.length === 0 ? rowData : tableFilterDelete,
      )
    } else {
      objectPath.set(dataCheck, key, tableFilterDelete.concat(rowData))
    }
  } else {
    objectPath.set(dataCheck, key, tableFilterDelete)
  }

  return dataCheck
}

const callRuleApi = async (api = {}, data = {}, designContext = {}) => {
  let method = String(api.method || '').toLowerCase()

  if (method === 'get') return getData(api, data, designContext, true)
  if (method === 'post') return postData(api, data, designContext)
  if (method === 'put') return putData(api, data, designContext)
  if (method === 'patch') return patchData(api, data, designContext)
  if (method === 'delete') return deleteData(api, data, designContext)

  return null
}

const setRuleCallApiLoading = (designContext = {}, apiKey, value) => {
  let { isUiChanged = {}, setIsUiChanged } = designContext
  if (!apiKey || !setIsUiChanged) return

  if (value) {
    objectPath.set(isUiChanged, ['callAPI', apiKey], true)
  } else {
    objectPath.del(isUiChanged, ['callAPI', apiKey])
  }
  setIsUiChanged(isUiChanged)
}

const setRuleCallApiResponse = ({
  api,
  data,
  designContext,
  formData,
  responseData,
}) => {
  let { jsonForm = {}, setDatasourceValue } = designContext
  let datasourceValue =
    data.datasourceValue ||
    objectPath.get(designContext, '@datasource') ||
    designContext.datasourceValue ||
    {}
  let isWaitUpsert = objectPath.get(designContext, 'isUiChanged.isWaitUpsert')

  formData = mapResponseData(
    api,
    responseData,
    formData,
    isWaitUpsert,
    jsonForm,
    designContext,
  )

  objectPath.set(formData, `@response.${api.key}`, responseData)
  objectPath.set(data, ['response', api.key], responseData)
  data.formData = formData

  if (api.dataKey) {
    objectPath.set(
      datasourceValue,
      api.key,
      objectPath.get(responseData, api.dataKey),
    )
  } else {
    objectPath.set(datasourceValue, api.key, responseData)
  }
  data.datasourceValue = datasourceValue

  if (setDatasourceValue) setDatasourceValue(datasourceValue)

  return formData
}

const updateRuleCallApiWaitUpsert = (
  designContext = {},
  formData = {},
  dataCheck = {},
) => {
  let { isUiChanged = {}, setIsUiChanged } = designContext
  let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')

  if (!isWaitUpsert?.status) return

  let { key, isModalProp, isReplace, isApiTable } = isWaitUpsert
  let comingTable = objectPath.get(formData, `@isWaitUpsert.${key}`) || []
  let currentTable = objectPath.get(formData, `@isConfirmUpsert.${key}`) || []

  if (isModalProp) {
    if (!isReplace) {
      currentTable = currentTable.concat(comingTable)
    }
    objectPath.set(dataCheck, key, currentTable)
    objectPath.set(formData, `@isConfirmUpsert.${key}`, currentTable)
  }
  if (isApiTable) {
    objectPath.set(isUiChanged, 'isWaitFetchApiTable', true)
  }
  if (setIsUiChanged) setIsUiChanged(isUiChanged)
}

const runRuleCallApi = async (
  designContext = {},
  action = {},
  data = {},
  rule = {},
) => {
  let api = getRuleCallApi(designContext, action)
  if (!api) return null

  let dataCheck = getRuleCallApiRequestFormData(designContext, data)
  let requestData = getRuleCallApiData(designContext, data, dataCheck)
  let apiKey = api.key
  let response

  if (data.preloadedCallAPIKeys?.includes(apiKey)) {
    return null
  }

  if (
    apiKey &&
    objectPath.get(designContext, ['isUiChanged', 'callAPI', apiKey])
  ) {
    return null
  }

  setRuleCallApiLoading(designContext, apiKey, true)
  try {
    response = await callRuleApi(api, requestData, designContext)
    if (!response) return null

    let responseData = response?.data ?? {}
    let formData = setRuleCallApiResponse({
      api,
      data,
      designContext,
      formData: data.formData || dataCheck,
      responseData,
    })

    updateRuleCallApiWaitUpsert(designContext, formData, dataCheck)

    if (response.status >= 200 && response.status < 300) {
      await fetchAfterSuccess({
        action: api,
        designContext,
        formData,
      })
    }

    if (designContext.setFormData) designContext.setFormData(formData)

    return {
      api,
      data,
      response,
      rule,
    }
  } finally {
    setRuleCallApiLoading(designContext, apiKey, false)
  }
}

const addPendingCallApiAction = (key, promise) => {
  if (!key) return

  let pending = pendingCallApiActions[key] || []
  pending.push(promise)
  pendingCallApiActions[key] = pending

  promise.finally(() => {
    pendingCallApiActions[key] = (pendingCallApiActions[key] || []).filter(
      (item) => item !== promise,
    )
    if (pendingCallApiActions[key].length === 0) {
      delete pendingCallApiActions[key]
    }
  })
}

const waitPendingCallApiAction = async (key) => {
  let pending = pendingCallApiActions[key] || []
  if (pending.length === 0) return

  await Promise.allSettled(pending)
}

export const onRuleCallApi = async (
  designContext = {},
  action = {},
  data = {},
  rule = {},
) => {
  let actionKey = getRuleCallApiActionKey(rule, action)

  if (isRuleCallApiWaitMode(action)) {
    await waitPendingCallApiAction(actionKey)
  }

  let promise = runRuleCallApi(designContext, action, data, rule).catch(
    (error) => {
      console.error('[RuleCallApi] call api error', error)
      return null
    },
  )

  addPendingCallApiAction(actionKey, promise)

  return promise
}
